"use client";

import { useState, useEffect, useContext } from "react";
import { useAuth } from "../lib/hooks/useAuth";
import { Search, Plus, Map, Users, Settings, Bell, ChevronDown, Menu, X, Trash2, Loader2, Clock } from "lucide-react";
import { getDocuments, addDocument, updateCustomer, deleteDocument, updateDocument } from "../lib/firebase/firebaseUtils";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarFooter,
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarProvider,
  SidebarContext 
} from "./ui/sidebar";
import { ThemeToggle } from "./ui/theme-toggle";
import { cn } from "../lib/utils";
import React from "react";
import { useTheme } from "next-themes"
import Image from "next/image"
import { useRouter, usePathname } from 'next/navigation';
import { Header } from "./Header";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import Link from "next/link";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CLIENT_TYPES, CLIENT_STATUSES, type CustomerDetails, type ClientType, type ClientStatus } from "@/types/customer";
import { Badge } from "./ui/badge";
import { ClientBadge } from "@/components/ui/client-badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ThemeLogo } from "@/components/ThemeLogo";
import { formatDistanceToNow } from 'date-fns';
import { CLIENT_TYPE_COLORS } from "@/types/customer";
import { SidebarNav } from "./ui/sidebar-nav";
import { onSnapshot, collection } from 'firebase/firestore';
import { db } from "@/lib/firebase/firebase";
import { CreateCustomerDialog } from "@/components/CreateCustomerDialog";
import { CreateMapDialog } from "@/components/CreateMapDialog";
import { toast } from "@/components/ui/use-toast";
import { StatCardSkeleton, CustomerListItemSkeleton, CustomerTypesSkeleton } from "./ui/skeleton";

const getTypeGradient = (type: ClientType, theme: string | undefined) => {
  const gradients = {
    'Golf Course': {
      dark: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
      light: 'from-emerald-100 to-emerald-50 border-emerald-200'
    },
    'University': {
      dark: 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
      light: 'from-blue-100 to-blue-50 border-blue-200'
    },
    'Municipality': {
      dark: 'from-purple-500/10 to-purple-500/5 border-purple-500/20',
      light: 'from-purple-100 to-purple-50 border-purple-200'
    },
    'School': {
      dark: 'from-yellow-500/10 to-yellow-500/5 border-yellow-500/20',
      light: 'from-yellow-100 to-yellow-50 border-yellow-200'
    },
    'High End Residential': {
      dark: 'from-pink-500/10 to-pink-500/5 border-pink-500/20',
      light: 'from-pink-100 to-pink-50 border-pink-200'
    },
    'Athletic Facility': {
      dark: 'from-orange-500/10 to-orange-500/5 border-orange-500/20',
      light: 'from-orange-100 to-orange-50 border-orange-200'
    },
    'Government': {
      dark: 'from-red-500/10 to-red-500/5 border-red-500/20',
      light: 'from-red-100 to-red-50 border-red-200'
    },
    'Commercial': {
      dark: 'from-indigo-500/10 to-indigo-500/5 border-indigo-500/20',
      light: 'from-indigo-100 to-indigo-50 border-indigo-200'
    },
    'Other': {
      dark: 'from-slate-500/10 to-slate-500/5 border-slate-500/20',
      light: 'from-slate-100 to-slate-50 border-slate-200'
    }
  } as const;
  
  return theme === 'dark' ? gradients[type].dark : gradients[type].light;
};

const sortedClientTypes = (customers: CustomerDetails[], types: readonly string[]): ClientType[] => {
  return [...types].sort((a, b) => {
    const countA = customers.filter(c => c.type === a).length;
    const countB = customers.filter(c => c.type === b).length;
    return countB - countA;
  }) as ClientType[];
};

// Define a type for valid color keys
type ColorKey = 'emerald' | 'yellow' | 'blue' | 'purple' | 'pink' | 'orange' | 'red' | 'indigo' | 'slate';

const CustomerTypeProgressBar = ({ 
  percentage, 
  color,
  theme 
}: { 
  percentage: number; 
  color: ColorKey;
  theme: string | undefined;
}) => {
  const colorMap: Record<ColorKey, { light: string; dark: string }> = {
    'emerald': { light: '#059669', dark: '#10B981' }, // Emerald-600/500
    'yellow': { light: '#CA8A04', dark: '#EAB308' },  // Yellow-600/500
    'blue': { light: '#2563EB', dark: '#3B82F6' },    // Blue-600/500
    'purple': { light: '#7C3AED', dark: '#8B5CF6' },  // Purple-600/500
    'pink': { light: '#DB2777', dark: '#EC4899' },    // Pink-600/500
    'orange': { light: '#EA580C', dark: '#F97316' },  // Orange-600/500
    'red': { light: '#DC2626', dark: '#EF4444' },     // Red-600/500
    'indigo': { light: '#4F46E5', dark: '#6366F1' },  // Indigo-600/500
    'slate': { light: '#475569', dark: '#64748B' },   // Slate-600/500
  };

  return (
    <div className={cn(
      "w-full h-2 rounded-full",
      theme === 'dark' ? "bg-background/50" : "bg-gray-100"
    )}>
      <div 
        className="h-full rounded-full transition-all duration-300"
        style={{ 
          width: `${percentage}%`,
          backgroundColor: theme === 'dark' ? colorMap[color].dark : colorMap[color].light,
        }}
      />
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const router = useRouter();
  const [customers, setCustomers] = useState<CustomerDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { theme, systemTheme } = useTheme();
  const currentTheme = theme === 'system' ? systemTheme : theme;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [totalMaps, setTotalMaps] = useState(0);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [isCreateMapOpen, setIsCreateMapOpen] = useState(false);

  const handleCustomerCreated = (newCustomer: CustomerDetails) => {
    setCustomers(prev => [...prev, newCustomer]);
    setIsCreateCustomerOpen(false);
    router.push(`/customers/${newCustomer.id}`);
  };

  const handleCreateMap = async (data: { 
    name: string; 
    customer: { 
      id: string; 
      name: string; 
    }; 
    category: 'GOLF' | 'NORMAL';
  }) => {
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });

      const now = new Date().toISOString();
      const mapData = {
        name: data.name,
        customerId: data.customer.id,
        customerName: data.customer.name,
        category: data.category,
        status: 'New',
        lastModified: now,
        createdAt: now,
        center: {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        },
        zoom: 18,
        zones: [],
        markers: [],
        measurements: [],
        userId: user?.uid
      };

      const newMap = await addDocument('maps', mapData);
      
      // Update customer's lastModified timestamp
      await updateDocument('customers', data.customer.id, {
        lastModified: now,
        totalMaps: (customers.find(c => c.id === data.customer.id)?.totalMaps || 0) + 1
      });

      // Update local state
      setCustomers(prev => prev.map(c => 
        c.id === data.customer.id 
          ? { 
              ...c, 
              lastModified: now,
              totalMaps: (c.totalMaps || 0) + 1
            }
          : c
      ));

      setIsCreateMapOpen(false);
      router.push(`/maps/${newMap.id}/edit`);
      toast({
        title: "Success",
        description: "Map created successfully",
      });
    } catch (error) {
      console.error('Error creating map:', error);
      // Fallback to default coordinates if geolocation fails
      const now = new Date().toISOString();
      const mapData = {
        name: data.name,
        customerId: data.customer.id,
        customerName: data.customer.name,
        category: data.category,
        status: 'New',
        lastModified: now,
        createdAt: now,
        center: { lat: 37.7749, lng: -122.4194 },
        zoom: 18,
        zones: [],
        markers: [],
        measurements: [],
        userId: user?.uid
      };

      const newMap = await addDocument('maps', mapData);
      
      // Update customer's lastModified timestamp
      await updateDocument('customers', data.customer.id, {
        lastModified: now,
        totalMaps: (customers.find(c => c.id === data.customer.id)?.totalMaps || 0) + 1
      });

      // Update local state
      setCustomers(prev => prev.map(c => 
        c.id === data.customer.id 
          ? { 
              ...c, 
              lastModified: now,
              totalMaps: (c.totalMaps || 0) + 1
            }
          : c
      ));

      setIsCreateMapOpen(false);
      router.push(`/maps/${newMap.id}/edit`);
      toast({
        title: "Success",
        description: "Map created successfully",
      });
    }
  };

  useEffect(() => {
    const loadCustomers = async () => {
      if (!user) return;
      try {
        const docs = await getDocuments("customers");
        const userCustomers = docs.filter(
          (doc) => doc.userId === user.uid
        ) as CustomerDetails[];
        setCustomers(userCustomers);
      } catch (error) {
        console.error("Error loading customers:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomers();
  }, [user]);

  useEffect(() => {
    const loadMapCount = async () => {
      if (!user) return;
      
      try {
        const allMaps = await getDocuments("maps");
        const userMaps = allMaps.filter(map => map.userId === user.uid);
        setTotalMaps(userMaps.length);

        // Set up real-time listener for maps collection
        const mapsRef = collection(db, 'maps');
        const unsubscribe = onSnapshot(mapsRef, (snapshot) => {
          if (user) { // Add null check for user
            const userMaps = snapshot.docs.filter(doc => doc.data().userId === user.uid);
            setTotalMaps(userMaps.length);
          }
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error loading map count:", error);
      }
    };

    loadMapCount();
  }, [user]);

  return (
    <SidebarProvider>
      <div className="flex h-screen overflow-hidden bg-background">
        <Sidebar className="border-r">
          <SidebarHeader>
            <div className="flex items-center bg-background p-2">
              <ThemeLogo />
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarNav />
          </SidebarContent>
          <SidebarFooter />
        </Sidebar>

        <div className="flex flex-col flex-1">
          <Header 
            showBackButton={false}
            showLogo={false}
            showHamburger={true}
            showGlobalSearch
          />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="flex flex-col space-y-1.5 mb-8">
                <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-sm text-muted-foreground">
                  Overview of your customers and maps
                </p>
                <div className="flex items-center gap-3 mt-6">
                  <Button 
                    onClick={() => setIsCreateMapOpen(true)}
                    size="lg"
                    variant="outline"
                    className={cn(
                      "bg-background hover:bg-secondary/80",
                      "border-dashed",
                      "transition-colors"
                    )}
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    New Map
                  </Button>
                  <Button 
                    onClick={() => setIsCreateCustomerOpen(true)}
                    size="lg"
                    className={cn(
                      "bg-primary hover:bg-primary/90",
                      "text-primary-foreground",
                      "transition-colors"
                    )}
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    New Customer
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card 
                  className={cn(
                    "bg-gradient-to-br transition-all",
                    "hover:shadow-md hover:scale-[1.01]",
                    "border-0 shadow-sm",
                    currentTheme === 'dark' 
                      ? "from-blue-500/10 to-indigo-500/5 border-blue-500/20"
                      : "from-white to-blue-50/50 shadow-blue-100/50"
                  )}
                  onClick={() => router.push('/customers')}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium text-muted-foreground">Total Customers</CardTitle>
                      <div className={cn(
                        "p-2 rounded-full",
                        currentTheme === 'dark' 
                          ? "bg-blue-500/10" 
                          : "bg-blue-50"
                      )}>
                        <Users className={cn(
                          "w-4 h-4",
                          currentTheme === 'dark'
                            ? "text-blue-400"
                            : "text-blue-500"
                        )} />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-semibold">{customers.length}</div>
                  </CardContent>
                </Card>

                <Card 
                  className={cn(
                    "bg-gradient-to-br transition-all",
                    "hover:shadow-md hover:scale-[1.01]",
                    "border-0 shadow-sm",
                    currentTheme === 'dark'
                      ? "from-green-500/10 to-emerald-500/5 border-green-500/20"
                      : "from-white to-emerald-50/50 shadow-emerald-100/50"
                  )}
                  onClick={() => router.push('/customers?status=Active')}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium text-muted-foreground">Active Customers</CardTitle>
                      <div className={cn(
                        "p-2 rounded-full",
                        currentTheme === 'dark' 
                          ? "bg-emerald-500/10" 
                          : "bg-emerald-50"
                      )}>
                        <Users className={cn(
                          "w-4 h-4",
                          currentTheme === 'dark'
                            ? "text-emerald-400"
                            : "text-emerald-500"
                        )} />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-semibold">
                      {customers.filter(c => c.status === 'Active').length}
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className={cn(
                    "bg-gradient-to-br transition-all",
                    "hover:shadow-md hover:scale-[1.01]",
                    "border-0 shadow-sm",
                    currentTheme === 'dark'
                      ? "from-purple-500/10 to-violet-500/5 border-purple-500/20"
                      : "from-white to-violet-50/50 shadow-violet-100/50"
                  )}
                  onClick={() => router.push('/maps')}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium text-muted-foreground">Total Maps</CardTitle>
                      <div className={cn(
                        "p-2 rounded-full",
                        currentTheme === 'dark' 
                          ? "bg-violet-500/10" 
                          : "bg-violet-50"
                      )}>
                        <Map className={cn(
                          "w-4 h-4",
                          currentTheme === 'dark'
                            ? "text-violet-400"
                            : "text-violet-500"
                        )} />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-semibold">{totalMaps}</div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <CardTitle>Recent Customers</CardTitle>
                        <CardDescription>Latest customer activity</CardDescription>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-muted-foreground hover:text-primary"
                        onClick={() => router.push('/customers')}
                      >
                        View all
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {customers
                        .sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime())
                        .slice(0, 5)
                        .map(customer => (
                          <div 
                            key={customer.id}
                            className={cn(
                              "p-3 rounded-lg transition-all",
                              "bg-gradient-to-br border-0",
                              "hover:shadow-md hover:scale-[1.01] cursor-pointer",
                              currentTheme === 'dark'
                                ? getTypeGradient(customer.type, currentTheme)
                                : "from-white to-slate-50/30 shadow-sm"
                            )}
                            onClick={() => router.push(`/customers/${customer.id}`)}
                          >
                            <div className="flex items-center justify-between space-x-4">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <div className={cn(
                                    "w-2 h-2 rounded-full",
                                    `bg-${CLIENT_TYPE_COLORS[customer.type]}-500`
                                  )} />
                                  <h3 className="text-sm font-medium truncate">{customer.name}</h3>
                                </div>
                                <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                                  <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span>{formatDistanceToNow(new Date(customer.lastModified))} ago</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Map className="h-3.5 w-3.5" />
                                    <span>{customer.totalMaps} maps</span>
                                  </div>
                                </div>
                              </div>
                              <ClientBadge 
                                type="type" 
                                value={customer.type}
                                className="shrink-0"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Customers by Type</CardTitle>
                    <CardDescription>Distribution of customers across different categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {sortedClientTypes(customers, CLIENT_TYPES).map((type: ClientType) => {
                        const count = customers.filter(c => c.type === type).length;
                        const total = customers.length;
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        const color = CLIENT_TYPE_COLORS[type];
                        
                        // Condensed view for types with 0 customers
                        if (count === 0) {
                          return (
                            <div 
                              key={type}
                              className={cn(
                                "p-3 rounded-lg border transition-all opacity-50",
                                "bg-gradient-to-br",
                                getTypeGradient(type, currentTheme),
                              )}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">{type}</span>
                                <span className="text-xs text-muted-foreground">No customers</span>
                              </div>
                            </div>
                          );
                        }

                        // Full view for types with customers
                        return (
                          <div 
                            key={type}
                            className={cn(
                              "p-4 rounded-lg transition-all",
                              "bg-gradient-to-br border-0",
                              "hover:shadow-md hover:scale-[1.01]",
                              currentTheme === 'dark'
                                ? getTypeGradient(type, currentTheme)
                                : "from-white to-slate-50/30 shadow-sm"
                            )}
                            onClick={() => router.push(`/customers?filter=${encodeURIComponent(type)}`)}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={cn(
                                  "w-2 h-2 rounded-full",
                                  `bg-${CLIENT_TYPE_COLORS[type]}-500`
                                )} />
                                <span className="text-sm font-medium">{type}</span>
                              </div>
                              <span className="text-sm font-medium">{percentage}%</span>
                            </div>
                            <div className="space-y-2">
                              <CustomerTypeProgressBar 
                                percentage={percentage} 
                                color={CLIENT_TYPE_COLORS[type] as ColorKey}
                                theme={currentTheme}
                              />
                              <div className="flex items-center text-xs text-muted-foreground">
                                <Users className={cn(
                                  "h-3.5 w-3.5 mr-1.5",
                                  `text-${CLIENT_TYPE_COLORS[type]}-500`
                                )} />
                                <span>{count} customer{count !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <CreateCustomerDialog
                open={isCreateCustomerOpen}
                onOpenChange={setIsCreateCustomerOpen}
                onComplete={handleCustomerCreated}
              />

              <CreateMapDialog
                open={isCreateMapOpen}
                onOpenChange={setIsCreateMapOpen}
                customers={customers}
                onCreateMap={handleCreateMap}
              />
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
} 